import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { mediaMSmMax } from 'helpers/breakpoints';
import FadeInSection from 'components/homepage/fadeInSection';
import { useAnimationHook } from 'components/homepage/animationUtils';
import { BrandReview } from 'components/homepage/brandReview';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import { useBreakpoint } from 'components/BreakpointProvider';
import NextImageWithShimmer from 'components/image/NextImageWithShimmer';
import Link from 'next/link';
import GooglePlay from 'components/icons/social/GooglePlay';
import AppStore from 'components/icons/social/AppStore';
import boatsetterPaths from 'utils/boatsetterPaths';

const SocialProof = () => {
  const [isVisible, setVisible] = useState(false);
  const [available, setAvailable] = useState(false);
  const [location, setLocation] = useState(false);
  const [make, setMake] = useState(false);
  const [captain, setCaptain] = useState(false);
  const [earning, setEarning] = useState(false);
  const domRef = useRef();
  const breakpoints = useBreakpoint();
  const isMobile = breakpoints.xs || breakpoints.sm;

  useAnimationHook(domRef, setVisible);

  const onChangeAvailable = (visible) => {
    setAvailable(visible);
  };
  const onChangeLocation = (visible) => {
    setLocation(visible);
  };
  const onChangeMake = (visible) => {
    setMake(visible);
  };
  const onChangeCaptain = (visible) => {
    setCaptain(visible);
  };
  const onChangeEarning = (visible) => {
    setEarning(visible);
  };

  return (
    <MainContainer>
      <div className="mission">
        <img src="/images/about/mission-quote.png" alt="Quote" />
        <p>COMPANY MISSION</p>
        <h2>
          “Provide boating and on-the-water experiences for everyone, <i>everywhere.</i>”
        </h2>
      </div>
      <div id="aboutReviews" className="reviews">
        <FadeInSection>
          <BrandReview />
        </FadeInSection>
      </div>
      <div className="user-app">
        <FadeInSection isSlow isSmallerMargin isVisibleProp={isVisible}>
          <div className="imgWrap">
            <NextImageWithShimmer
              src={isMobile ? 'spa/about/main-app-img-mob.png' : 'spa/about/main-app-img.png'}
              alt="Share a boat"
              height={376}
              width={965}
              style={{
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'cover',
              }}
            />
          </div>
        </FadeInSection>
        <FadeInSection isSlow isRight isVisibleProp={isVisible} isSmallerMargin>
          <div className="small">
            <p className="main-text">
              Rent a boat today, with or without a captain. It’s easy, affordable and{' '}
              <Link href="/">available near you »</Link>
            </p>
            <div className="icon-section">
              <img src="/images/about/main-app-icon.svg" alt="" />
              <div className="right">
                <p>The Boatsetter App</p>
                <span>Find & book boats in over 700+ locations</span>
              </div>
            </div>
            <div className="app-row">
              <a
                className="appstore"
                href="https://apps.apple.com/us/app/boatsetter-boat-rentals/id1449873080"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStore width={98} height={27} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.boatsetterrenters&hl=en_US&gl=US"
                target="_blank"
                rel="noopener noreferrer"
              >
                <GooglePlay width={98} height={27} />
              </a>
            </div>
          </div>
        </FadeInSection>
      </div>
      <div className="numbers">
        <h2>BOATSETTER BY THE NUMBERS</h2>
        <div className="numbersRow">
          <div className="numbersRow-unit">
            <img src="/images/about/icon1.svg" alt="" />
            <p>
              <VisibilitySensor partialVisibility scrollCheck onChange={onChangeAvailable} active={!available}>
                {({ isVisible: visible }) => (
                  <span>
                    {visible ? (
                      <CountUp end={50000} duration={1} formattingFn={(num) => num.toLocaleString()} />
                    ) : (
                      '50,000'
                    )}
                  </span>
                )}
              </VisibilitySensor>
              <span className="plus">+</span>
            </p>
            <span className="bottom-text">Boats available</span>
          </div>
          <div className="numbersRow-unit">
            <img src="/images/about/icon2.svg" alt="" />
            <p>
              <VisibilitySensor partialVisibility scrollCheck onChange={onChangeLocation} active={!location}>
                {({ isVisible: visible }) => (
                  <span>
                    {visible ? (
                      <CountUp end={700} duration={1.2} formattingFn={(num) => num.toLocaleString()} />
                    ) : (
                      '700'
                    )}
                  </span>
                )}
              </VisibilitySensor>
              <span className="plus">+</span>
            </p>
            <span className="bottom-text">Locations</span>
          </div>
          <div className="numbersRow-unit">
            <img src="/images/about/icon3.svg" alt="" />
            <p>
              <VisibilitySensor partialVisibility scrollCheck onChange={onChangeMake} active={!make}>
                {({ isVisible: visible }) => (
                  <span>
                    {visible ? (
                      <CountUp end={20000} duration={1.4} formattingFn={(num) => num.toLocaleString()} />
                    ) : (
                      '20,000'
                    )}
                  </span>
                )}
              </VisibilitySensor>
              <span className="plus">+</span>
            </p>
            <span className="bottom-text">Makes and models</span>
          </div>
          <div className="numbersRow-unit">
            <img src="/images/about/icon4.svg" alt="" />
            <p>
              <VisibilitySensor partialVisibility scrollCheck onChange={onChangeCaptain} active={!captain}>
                {({ isVisible: visible }) => (
                  <span>
                    {visible ? (
                      <CountUp end={1500} duration={1.6} formattingFn={(num) => num.toLocaleString()} />
                    ) : (
                      '1,500'
                    )}
                  </span>
                )}
              </VisibilitySensor>
              <span className="plus">+</span>
            </p>
            <span className="bottom-text">Captains</span>
          </div>
          <div className="numbersRow-unit">
            <img src="/images/about/icon5.svg" alt="" />
            <p>
              $
              <VisibilitySensor partialVisibility scrollCheck onChange={onChangeEarning} active={!earning}>
                {({ isVisible: visible }) => (
                  <span>
                    {visible ? (
                      <CountUp end={20000} duration={1.8} formattingFn={(num) => num.toLocaleString()} />
                    ) : (
                      '20,000'
                    )}
                  </span>
                )}
              </VisibilitySensor>
              <span className="plus">+</span>
            </p>
            <span className="bottom-text">Yearly earning potential</span>
          </div>
        </div>
      </div>
      <div className="owner-app">
        <FadeInSection isSlow isRight isSmallerMargin isVisibleProp={isVisible}>
          <div className="imgWrap">
            <NextImageWithShimmer
              src={isMobile ? 'spa/about/owner-app-img-mob.png' : 'spa/about/owner-app-img.png'}
              alt="Share a boat"
              height={376}
              width={965}
              style={{
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'cover',
              }}
            />
          </div>
        </FadeInSection>
        <FadeInSection isSlow isVisibleProp={isVisible} isSmallerMargin>
          <div className="small">
            <p className="main-text">
              Boatsetter is enabling entrepreneurship on the water. See how much money you can{' '}
              <Link href={boatsetterPaths.whyList}>earn by renting out your boat »</Link>
            </p>
            <div className="icon-section">
              <img src="/images/about/owner-app-icon.svg" alt="" />
              <div className="right">
                <p>Boatsetter Owner App</p>
                <span>Earn money renting your boat</span>
              </div>
            </div>
            <div className="app-row">
              <a
                className="appstore"
                href="https://apps.apple.com/us/app/boatsetter-owner-app/id1369215908"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStore width={98} height={27} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.boatsetter.app&hl=en_US&gl=US"
                target="_blank"
                rel="noopener noreferrer"
              >
                <GooglePlay width={98} height={27} />
              </a>
            </div>
          </div>
        </FadeInSection>

        <div className="mobile-border" />
      </div>
    </MainContainer>
  );
};

export default SocialProof;

const MainContainer = styled.div`
  padding: 0 0 80px 0;
  max-width: 1100px;
  margin: 0 auto;
  border-bottom: 1px solid #dbdfe5;

  ${mediaMSmMax} {
    border-bottom: none;
    padding-bottom: 120px;
  }

  .mission {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 0 60px 0;
    box-sizing: border-box;

    ${mediaMSmMax} {
      padding: 10px 0 30px 0;
      margin: 0 22px;
      box-sizing: border-box;
    }

    img {
      height: 58px;
      width: 75px;
      margin-bottom: 47px;

      ${mediaMSmMax} {
        width: 39px;
        height: 32px;
        margin-bottom: 28px;
      }
    }

    p {
      margin: 0 0 8px 0;
      font-style: normal;
      font-weight: 600;
      font-size: 18px;
      line-height: 25px;
      text-align: center;
      letter-spacing: 0.25em;
      color: #17233c;

      ${mediaMSmMax} {
        font-size: 14px;
        line-height: 19px;
      }
    }

    h2 {
      width: 914px;
      font-style: normal;
      font-weight: 600;
      font-size: 34px;
      line-height: 120%;
      text-align: center;
      letter-spacing: 0.5px;
      color: #000000;

      ${mediaMSmMax} {
        width: 100%;
        font-size: 24px;
        line-height: 120%;
        margin-top: 5px;
      }
    }
  }

  .numbers {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 110px;

    ${mediaMSmMax} {
      margin-top: 180px;
    }

    > h2 {
      font-style: normal;
      font-weight: 600;
      font-size: 18px;
      line-height: 25px;
      text-align: center;
      letter-spacing: 0.25em;
      color: #17233c;
      margin: 0;

      ${mediaMSmMax} {
        font-size: 14px;
        line-height: 19px;
        width: 165px;
      }
    }

    .numbersRow {
      display: flex;
      justify-content: space-around;
      margin-top: 33px;
      width: 100%;

      ${mediaMSmMax} {
        flex-direction: column;
        width: unset;
      }

      .numbersRow-unit {
        display: flex;
        flex-direction: column;
        flex: 1;

        ${mediaMSmMax} {
          flex: unset;
          margin-bottom: 20px;
        }

        img {
          height: 21px;
          margin-bottom: 18px;
        }

        p {
          font-style: normal;
          font-weight: bold;
          font-size: 30px;
          line-height: 24px;
          text-align: center;
          letter-spacing: -1px;
          color: #17233c;
          margin-bottom: 8px;
          margin-top: 0;
          font-variant-numeric: tabular-nums;

          .plus {
            font-weight: 400;
          }
        }

        .bottom-text {
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 24px;
          text-align: center;
          color: #17233c;
        }
      }
    }
  }

  .user-app {
    margin-top: 90px;
    position: relative;

    ${mediaMSmMax} {
      margin-top: 75px;
    }

    .imgWrap {
      border-radius: 10px;
      overflow: hidden;
      width: 965px;
      height: 376px;
      display: flex;

      ${mediaMSmMax} {
        width: 80%;
        margin-left: -18px;
      }
    }

    .small {
      padding: 32px;
      box-sizing: border-box;
      width: 313px;
      background: linear-gradient(132.02deg, #033889 14.38%, #0751c2 54.76%, #548ee6 102.97%);
      position: absolute;
      bottom: 55px;
      right: 0;

      ${mediaMSmMax} {
        right: 22px;
        bottom: -120px;
        width: 290px;
        padding: 28px;
      }

      .main-text {
        font-style: normal;
        font-weight: 600;
        font-size: 21px;
        line-height: 130%;
        color: #ffffff;

        a {
          color: #f5c064;
          text-decoration: none;
        }
      }

      .icon-section {
        display: flex;

        img {
          width: 30px;
          height: 30px;
        }

        .right {
          margin-left: 12px;

          p {
            font-style: normal;
            font-weight: 600;
            font-size: 14px;
            line-height: 15px;
            display: flex;
            align-items: center;
            color: #ffffff;
            margin: 0 0 2px 0;
          }

          span {
            font-style: normal;
            font-weight: 500;
            font-size: 11px;
            line-height: 15px;
            display: flex;
            align-items: center;
            letter-spacing: -0.2px;
            color: #ffffff;
            opacity: 0.6;
          }
        }
      }

      .app-row {
        margin-top: 15px;

        .appstore {
          margin-right: 12px;
        }

        img {
          cursor: pointer;
        }
      }
    }
  }

  .reviews {
    margin-top: -200px;
    margin-bottom: -120px;

    ${mediaMSmMax} {
      margin-top: -50px;
      margin-bottom: -100px;
    }
  }

  .owner-app {
    margin-top: 120px;
    margin-bottom: 50px;
    position: relative;

    ${mediaMSmMax} {
      margin-top: 100px;
    }

    .mobile-border {
      ${mediaMSmMax} {
        border-bottom: 1px solid #dbdfe5;
        margin: 0 22px;
        left: 0;
        right: 0;
        position: absolute;
        bottom: -240px;
      }
    }

    .imgWrap {
      border-radius: 10px;
      overflow: hidden;
      width: 965px;
      height: 376px;
      z-index: 1;
      display: flex;
      position: absolute;
      z-index: 1;
      right: 0;
      top: -32px;

      ${mediaMSmMax} {
        width: 80%;
        margin-right: -18px;
      }
    }

    .small {
      padding: 32px;
      box-sizing: border-box;
      width: 313px;
      background: linear-gradient(143.96deg, #084240 -1.36%, #0e7873 41.71%, #589a6b 151.43%);
      position: relative;
      z-index: 2;

      ${mediaMSmMax} {
        left: 22px;
        bottom: -195px;
        width: 290px;
        padding: 28px;
      }

      .main-text {
        font-style: normal;
        font-weight: 600;
        font-size: 21px;
        line-height: 130%;
        color: #ffffff;

        a {
          color: #f5c064;
          text-decoration: none;
        }
      }

      .icon-section {
        display: flex;

        img {
          width: 30px;
          height: 30px;
        }

        .right {
          margin-left: 12px;

          p {
            font-style: normal;
            font-weight: 600;
            font-size: 14px;
            line-height: 15px;
            display: flex;
            align-items: center;
            color: #ffffff;
            margin: 0 0 2px 0;
          }

          span {
            font-style: normal;
            font-weight: 500;
            font-size: 11px;
            line-height: 15px;
            display: flex;
            align-items: center;
            letter-spacing: -0.2px;
            color: #ffffff;
            opacity: 0.6;
          }
        }
      }

      .app-row {
        margin-top: 15px;

        .appstore {
          margin-right: 12px;
        }

        img {
          cursor: pointer;
        }
      }
    }
  }
`;
